import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { AppLocale } from '@/shared/lib';

import { applyHeadTags, buildHeadTags, type HeadTags, type SeoInput } from './headTags';
import { SeoCollectorContext } from './seoContext';

type SeoProps = Omit<SeoInput, 'locale' | 'path'> & {
  /** Defaults to the current route. */
  path?: string;
};

/**
 * Declares the current page's <head>: title, description, canonical, hreflang
 * alternates, social cards, robots and JSON-LD. Render one per route.
 *
 * During prerendering the tags go to the collector and are written into the
 * static HTML; in the browser they replace the previous page's tags.
 */
export const Seo = ({ path, ...input }: SeoProps) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const collect = useContext(SeoCollectorContext);

  const tags = buildHeadTags({
    ...input,
    locale: i18n.language as AppLocale,
    path: path ?? pathname,
  });
  collect?.(tags);

  const serialized = JSON.stringify(tags);
  useEffect(() => {
    applyHeadTags(JSON.parse(serialized) as HeadTags);
  }, [serialized]);

  return null;
};
